// SPDX-License-Identifier: MIT
// Desktop AI bridge (step 2 + Point A). Routes inference from the user's machine,
// never a Genesis server — preserving the BYOAI / zero-server-inference north-star:
//   - default      → the user's LOCAL `claude` CLI (their subscription), via the
//                    Rust `claude_prompt` command.
//   - sovereign     → an OpenAI-compatible endpoint the user *connects* (ai-lab's
//                    vLLM / LiteLLM), via the Rust `sovereign_prompt` command. The
//                    machine reaches the endpoint directly — zero VPS dependency.
// Which one is chosen comes from the desktop-local provider config (provider-store).
// Uses the global window.__TAURI__ invoke so the web build never imports @tauri-apps.
import type { AiBridge } from './types';
import { parseFicheJson, unwrapClaudeEnvelope } from './claude-output';
import { unwrapSovereignBody } from './sovereign-output';
import { parseConverseOutput } from './converse-output';
import { parseDiscoverOutput } from './discover-output';
import { buildMagnifyPrompt, buildConversePrompt, buildDiscoverPrompt } from './prompts';
import { tauriInvoke } from './tauri';
import { readDesktopProviderConfig } from './provider-store';
import { getConversation, appendTurn, newConversationId } from './conversation-store';

// `claude -p <prompt> --output-format json` on the user's subscription → raw stdout.
async function runClaude(prompt: string): Promise<string> {
  return (await tauriInvoke()('claude_prompt', { prompt })) as string;
}

// POST <endpoint>/chat/completions on the user's sovereign endpoint → raw HTTP body.
async function runSovereign(
  endpoint: string,
  model: string,
  key: string | null,
  prompt: string,
): Promise<string> {
  return (await tauriInvoke()('sovereign_prompt', { endpoint, model, key, prompt })) as string;
}

// Shared routing: send a prompt to the user's chosen provider (sovereign endpoint
// if configured, else their local Claude subscription) and return the UNWRAPPED
// inner text (the model's answer, envelope stripped). Used by magnify + converse.
async function promptToInner(prompt: string): Promise<string> {
  const cfg = await readDesktopProviderConfig();
  if (cfg?.kind === 'sovereign' && cfg.endpoint_url && cfg.model) {
    return unwrapSovereignBody(await runSovereign(cfg.endpoint_url, cfg.model, cfg.api_key ?? null, prompt));
  }
  return unwrapClaudeEnvelope(await runClaude(prompt));
}

export const desktopAiBridge: AiBridge = {
  async magnify(idea, locale, attachmentsContext) {
    const inner = await promptToInner(buildMagnifyPrompt(idea, locale, attachmentsContext));
    return parseFicheJson(inner);
  },

  // Multi-turn converse (step 4). The bridge plays the role the web edge fn does
  // server-side: it holds the conversation history (conversation-store) and asks
  // the model to reply + decide whether the fiche materially changed (the model
  // decides — version bumps only on real changes). BYOAI: runs on the user's
  // subscription or their sovereign endpoint, never a Genesis server.
  async converse(projectId, message, locale, conversationId, currentManifest, currentVersion) {
    if (!currentManifest) throw new Error('[desktop] converse requires a current manifest (magnify first)');
    const convId = conversationId ?? newConversationId(projectId);
    const history = await getConversation(convId);
    const inner = await promptToInner(buildConversePrompt(history, message, locale, currentManifest));
    const out = parseConverseOutput(inner);
    const changed = out.manifest_changed && !!out.manifest;
    const versionNumber = changed ? (currentVersion ?? 1) + 1 : null;
    // Persist the badge metadata on the assistant turn so a reload shows which
    // turn evolved the fiche (mirrors what the web edge fn writes to messages).
    await appendTurn(convId, message, out.reply, {
      manifest_changed: out.manifest_changed,
      version_number: versionNumber,
    });
    return {
      conversation_id: convId,
      reply: out.reply,
      manifest_changed: out.manifest_changed,
      ...(changed ? { version_number: versionNumber!, manifest: out.manifest } : {}),
    };
  },

  // Discovery — TEXT path. The web edge fn reads the project's Storage
  // attachments multimodally ; on desktop we run the discovery prompt over the
  // EXTRACTED TEXT of the user's files (passed in by the caller — the desktop
  // has no Supabase). Multimodal (images/PDF via vision) is deferred until
  // ai-lab is delivered (it sequences discover last). Runs on the user's own
  // subscription / sovereign endpoint, like magnify + converse.
  async discover(_projectId, ctx) {
    if (!ctx || ctx.textContext.trim().length === 0) {
      // status 400 → the discovery CTA shows the "no readable files" message.
      throw Object.assign(new Error('[desktop] discover needs readable file text — add some files first'), { status: 400 });
    }
    const inner = await promptToInner(buildDiscoverPrompt(ctx.idea, ctx.locale, ctx.textContext, ctx.seen ?? []));
    const parsed = parseDiscoverOutput(inner);
    return {
      discovery_summary: parsed.discovery_summary,
      questions: parsed.questions,
      attachments_seen: ctx.seen ?? [],
      attachments_skipped: ctx.skipped ?? [],
    };
  },
};
